// The pause reasons that mean no Lead Agent plan (a commercial pause). Their
// banner is the only one that can be closed: by the Owner for the whole
// workspace (AGI-766, MLA-100) or by the Admin in this browser (AGI-777).
// Every other reason keeps showing.
export const COMMERCIAL_PAUSE_REASONS: ReadonlySet<string> = new Set([
  'acceptance_lapsed',
  'no_active_tier',
]);

/**
 * Whether a token-availability pause reason is a commercial pause.
 *
 * @param reason - The pause reason, or null when there is none.
 * @returns `true` for `acceptance_lapsed` and `no_active_tier`.
 */
export function isCommercialPauseReason(reason: string | null): boolean {
  return reason !== null && COMMERCIAL_PAUSE_REASONS.has(reason);
}

// Dismissals of the commercial billing-paused banner, per workspace and user.
// The in-memory copy keeps a dismissal for the session when storage throws
// (private mode, blocked site data), including when only the write fails.
const leadAgentPausedBannerDismissedKeys = new Set<string>();

/**
 * Reports whether the billing-paused banner was dismissed under this key,
 * in this session or in this browser's storage.
 *
 * @param key - The dismissal key, built from the workspace id and the user id.
 * @returns `true` when the banner was dismissed.
 */
export function isLeadAgentPausedBannerDismissed(key: string): boolean {
  if (leadAgentPausedBannerDismissedKeys.has(key)) return true;
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/**
 * Stores a dismissal of the billing-paused banner for the session and, when
 * storage allows it, for this browser.
 *
 * @param key - The dismissal key, built from the workspace id and the user id.
 */
export function storeLeadAgentPausedBannerDismissal(key: string): void {
  leadAgentPausedBannerDismissedKeys.add(key);
  try {
    localStorage.setItem(key, '1');
  } catch {
    // The in-memory copy above already holds the dismissal.
  }
}

/**
 * Clears the in-memory dismissals of the billing-paused banner.
 *
 * The set lives at module level, so it survives between the tests of one
 * file: a dismissal from one test would hide the banner in the next. Test
 * files that dismiss the banner call it in their `beforeEach`.
 */
export function resetLeadAgentPausedBannerDismissalsForTests(): void {
  leadAgentPausedBannerDismissedKeys.clear();
}
